import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import { useState, useCallback, useEffect } from 'react';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { GuidService } from 'ui-components/services/guid-service';

import { DefineShipmentsTable } from './define-shipments-table/define-shipments-table';
import styles from './define-shipments.module.less';
import { useDefineShipmentsTableData, useMapSelectedOptionsForDropdown } from './hooks';
import { messages } from './messages';
import { ConfirmDialogueModal } from '../../../../../../../components/modals/confirm-dialogue-modal';
import { useModal } from '../../../../../../../hooks/use-modal';
import { createLookupHash } from '../../../../../../../services/hash-service';
import { initialBoxDefaults, initialProductDefaults } from '../../../../constants';
import { useAdvancedShippingNoticeContext } from '../../advanced-shipping-notice-context';
import { TableInformation } from '../components/table-information';
import { BOXES } from '../constants';

const calculateInitialAvailableProducts = (selectedProducts, boxes) => {
  const productTotals = boxes.reduce((totals, box) => {
    box.products.forEach((product) => {
      if (!product.productId) return;
      const productQuantity = product.quantityPerBox * box.boxesPerConfig;
      // oxlint-disable-next-line no-param-reassign
      totals[product.productId] = (totals[product.productId] || 0) + productQuantity;
    });
    return totals;
  }, {});

  return selectedProducts.map((product) => ({
    ...product,
    quantityPerBox: 0,
    quantityRemaining: product.quantity - (productTotals[product.productId] || 0),
  }));
};

export const DefineShipmentsEdit = ({ stepPayload, setStepPayload, selectedProducts }) => {
  const { isParcelConfiguration, validationErrors } = useAdvancedShippingNoticeContext();
  const boxesCount = stepPayload[BOXES]?.length;
  const { isOpen, open, close } = useModal();

  const modalMessages = isParcelConfiguration
    ? {
        header: messages.deleteBoxesConfirmModalHeader,
        body: messages.deleteBoxesConfirmModalBody,
      }
    : {
        header: messages.deletePalletsConfirmModalHeader,
        body: messages.deletePalletsConfirmModalBody,
      };

  const [availableProducts, setAvailableProducts] = useState(() => {
    return calculateInitialAvailableProducts(selectedProducts, stepPayload[BOXES]);
  });

  const onAddNewBoxConfiguration = useCallback(() => {
    const addNewBox = {
      ...initialBoxDefaults,
      boxConfigurationId: GuidService.newGuid(),
      trackingNumber: GuidService.newGuid(),
      products: [{ ...initialProductDefaults, uniqueProductRowId: GuidService.newGuid() }],
    };
    setStepPayload((prevPayload) => ({
      ...prevPayload,
      [BOXES]: [...stepPayload[BOXES], addNewBox],
    }));
  }, [stepPayload, setStepPayload]);

  useEffect(() => {
    if (boxesCount === 0) {
      onAddNewBoxConfiguration();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisabledAddSkusButton = useCallback(
    (boxConfigurationId) => {
      const box = stepPayload[BOXES].find((b) => b.boxConfigurationId === boxConfigurationId);
      if (!box) return false;

      const boxProductIdsSet = new Set(box.products.map((product) => product.productId));
      return availableProducts.every((product) => boxProductIdsSet.has(product.productId));
    },
    [stepPayload, availableProducts],
  );

  const tableData = useDefineShipmentsTableData({
    stepPayload,
    isDisabledAddSkusButton,
  });

  const onAddProductToBoxConfiguration = (row) => () => {
    const { boxConfigurationId } = row?.original || {};
    setStepPayload((prevPayload) => ({
      ...prevPayload,
      [BOXES]: prevPayload[BOXES].map((box) => {
        if (box.boxConfigurationId === boxConfigurationId) {
          return {
            ...box,
            products: [...box.products, { ...initialProductDefaults, uniqueProductRowId: GuidService.newGuid() }],
          };
        }
        return box;
      }),
    }));
  };

  const onDeleteProduct = ({ boxConfigurationId, productId, quantityPerBox, quantityRemaining, boxesPerConfig }) => {
    setStepPayload((prevPayload) => {
      const updatedBoxes = prevPayload[BOXES].reduce((acc, box) => {
        if (box.boxConfigurationId === boxConfigurationId) {
          const updatedProducts = box.products.filter((product) => product.productId !== productId);
          if (updatedProducts.length > 0) {
            acc.push({ ...box, products: updatedProducts });
          }
        } else {
          acc.push({
            ...box,
            products: box.products.map((product) =>
              product.productId === productId
                ? {
                    ...product,
                    quantityRemaining: quantityPerBox * boxesPerConfig + quantityRemaining,
                  }
                : product,
            ),
          });
        }
        return acc;
      }, []);

      return {
        ...prevPayload,
        [BOXES]: updatedBoxes,
      };
    });

    setAvailableProducts((prevProducts) => {
      return prevProducts.map((product) => {
        if (product.productId !== productId) return product;

        const totalQuantityUsed =
          stepPayload[BOXES].reduce((total, box) => {
            return (
              total +
              box.products
                .filter((p) => p.productId === productId)
                .reduce((sum, p) => sum + (p.quantityPerBox || 0) * box.boxesPerConfig, 0)
            );
          }, 0) -
          quantityPerBox * boxesPerConfig;

        return {
          ...product,
          quantityRemaining: product.quantity - totalQuantityUsed,
        };
      });
    });
  };

  const onSelectProduct = useCallback(
    ({ productId, boxConfigurationId, uniqueProductRowId }) => {
      setStepPayload((prevPayload) => {
        const selectedProduct = availableProducts.find((p) => p.productId === productId);

        const totalQuantityInOtherBoxes = prevPayload[BOXES].reduce((sum, box) => {
          if (box.boxConfigurationId === boxConfigurationId) return sum;
          const productInBox = box.products.find((p) => p.productId === productId);
          return sum + (productInBox?.quantityPerBox || 0) * box.boxesPerConfig;
        }, 0);

        const currentBox = prevPayload[BOXES].find((box) => box.boxConfigurationId === boxConfigurationId);
        const remainingQuantity = selectedProduct.quantity - totalQuantityInOtherBoxes;
        const newQuantityPerBox = Math.max(Math.floor(remainingQuantity / currentBox.boxesPerConfig), 0);
        const totalWithNewQuantity = totalQuantityInOtherBoxes + newQuantityPerBox * currentBox.boxesPerConfig;
        const newQuantityRemaining = selectedProduct.quantity - totalWithNewQuantity;

        setAvailableProducts((prevProducts) => {
          return prevProducts.map((product) => {
            if (product.productId === productId) {
              return {
                ...product,
                quantityRemaining: newQuantityRemaining,
              };
            }
            return product;
          });
        });

        return {
          ...prevPayload,
          [BOXES]: prevPayload[BOXES].map((box) => {
            if (box.boxConfigurationId !== boxConfigurationId) {
              return {
                ...box,
                products: box.products.map((product) =>
                  product.productId === productId ? { ...product, quantityRemaining: newQuantityRemaining } : product,
                ),
              };
            }

            const updatedProducts = box.products.map((boxProduct) => {
              const isTargetProduct = boxProduct.uniqueProductRowId === uniqueProductRowId;

              return isTargetProduct
                ? {
                    ...selectedProduct,
                    quantityPerBox: newQuantityPerBox,
                    quantityRemaining: newQuantityRemaining,
                    uniqueProductRowId: boxProduct.uniqueProductRowId,
                  }
                : boxProduct;
            });

            return { ...box, products: updatedProducts };
          }),
        };
      });
    },
    [availableProducts, setStepPayload],
  );

  const onChangeQuantityPerBox = useCallback(
    ({ boxConfigurationId, productId, newQuantity }) => {
      const currentBoxes = stepPayload[BOXES];
      const totalQuantityInBoxes = currentBoxes.reduce((sum, box) => {
        if (box.boxConfigurationId === boxConfigurationId) return sum;
        const productInBox = box.products.find((p) => p.productId === productId);
        return sum + (productInBox?.quantityPerBox || 0) * box.boxesPerConfig;
      }, 0);

      const currentBox = currentBoxes.find((box) => box.boxConfigurationId === boxConfigurationId);
      const totalWithNewQuantity = totalQuantityInBoxes + newQuantity * currentBox.boxesPerConfig;

      // Update boxes with new quantity
      setStepPayload((prevPayload) => ({
        ...prevPayload,
        [BOXES]: prevPayload[BOXES].map((box) => {
          // current box
          if (box.boxConfigurationId === boxConfigurationId) {
            return {
              ...box,
              products: box.products.map((product) =>
                product.productId === productId
                  ? {
                      ...product,
                      quantityPerBox: newQuantity,
                      quantityRemaining: product.quantity - totalWithNewQuantity,
                    }
                  : product,
              ),
            };
          }

          // other boxes
          return {
            ...box,
            products: box.products.map((product) =>
              product.productId === productId
                ? {
                    ...product,
                    quantityRemaining: product.quantity - totalWithNewQuantity,
                  }
                : product,
            ),
          };
        }),
      }));

      // Update available products with remaining quantity
      setAvailableProducts((prevProducts) => {
        return prevProducts.map((product) => {
          if (product.productId === productId) {
            return {
              ...product,
              quantityRemaining: product.quantity - totalWithNewQuantity,
            };
          }
          return product;
        });
      });
    },
    [stepPayload, setStepPayload],
  );

  const calculateProductTotals = (boxList, boxConfigurationId, newQuantity) => {
    return boxList.reduce((totals, box) => {
      box.products.forEach((product) => {
        if (!product.productId) return;

        const boxQuantity = box.boxConfigurationId === boxConfigurationId ? newQuantity : box.boxesPerConfig;
        const productQuantity = boxQuantity * product.quantityPerBox;

        // oxlint-disable-next-line no-param-reassign
        totals[product.productId] = (totals[product.productId] || 0) + productQuantity;
      });
      return totals;
    }, {});
  };

  const updateProductQuantities = (products, productTotals) => {
    return products.map((product) => ({
      ...product,
      quantityRemaining: product.productId ? product.quantity - (productTotals[product.productId] || 0) : product.quantityRemaining,
    }));
  };

  const onChangeBoxesPerConfig = useCallback(
    ({ boxConfigurationId, newQuantity }) => {
      setStepPayload((prevPayload) => {
        const productTotals = calculateProductTotals(prevPayload[BOXES], boxConfigurationId, newQuantity);
        return {
          ...prevPayload,
          [BOXES]: prevPayload[BOXES].map((box) => ({
            ...box,
            boxesPerConfig: box.boxConfigurationId === boxConfigurationId ? newQuantity : box.boxesPerConfig,
            products: updateProductQuantities(box.products, productTotals),
          })),
        };
      });

      setAvailableProducts((prevProducts) => {
        const productTotals = calculateProductTotals(stepPayload[BOXES], boxConfigurationId, newQuantity);
        return updateProductQuantities(prevProducts, productTotals);
      });
    },
    [setStepPayload, stepPayload],
  );

  const allOptionsForDropdown = useMapSelectedOptionsForDropdown(availableProducts);

  const getOptionsForDropdown = useCallback(
    (boxConfigurationId) => {
      if (!allOptionsForDropdown) return [];
      const currentBox = stepPayload[BOXES].find((box) => box.boxConfigurationId === boxConfigurationId);
      if (!currentBox) {
        return allOptionsForDropdown;
      }

      const productLookup = createLookupHash(availableProducts, 'productId');
      const currentBoxProductIds = new Set(currentBox.products.filter((product) => product.productId).map((product) => product.productId));

      return allOptionsForDropdown.filter(({ value }) => {
        const product = productLookup[value];
        return product && (product.quantityRemaining > 0 || currentBoxProductIds.has(product.productId));
      });
    },
    [allOptionsForDropdown, availableProducts, stepPayload],
  );

  const getDisabledOptionsForDropdown = useCallback(
    (boxConfigurationId) => {
      const box = stepPayload[BOXES].find((b) => b.boxConfigurationId === boxConfigurationId);
      return box?.products.map((product) => product.productId);
    },
    [stepPayload],
  );

  const onClearAllBoxes = () => {
    setStepPayload((prevPayload) => ({
      ...prevPayload,
      [BOXES]: [],
    }));

    setAvailableProducts(
      selectedProducts.map((product) => ({
        ...product,
        quantityPerBox: 0,
        quantityRemaining: product.quantity,
      })),
    );

    close();
  };

  return (
    <>
      {validationErrors?.boxes?.length > 0 && (
        <BannerMessageV2
          content={
            <>
              <Text textType={TextType.Label} value={messages.quantityRemainingErrorHeader} />
              <Text textType={TextType.Caption} value={messages.quantityRemainingErrorBody} />
            </>
          }
          layout={BannerLayout.Full}
          status={BannerStatus.Error}
        />
      )}
      <TableInformation
        count={boxesCount}
        countInformationMessage={isParcelConfiguration ? messages.configuredBoxesCount : messages.configuredPalletsCount}
        handleClearAll={open}
      />
      <DefineShipmentsTable
        data={tableData}
        getDisabledOptionsForDropdown={getDisabledOptionsForDropdown}
        getOptionsForDropdown={getOptionsForDropdown}
        onAddNewBoxConfiguration={onAddNewBoxConfiguration}
        onAddProductToBoxConfiguration={onAddProductToBoxConfiguration}
        onChangeBoxesPerConfig={onChangeBoxesPerConfig}
        onChangeQuantityPerBox={onChangeQuantityPerBox}
        onDeleteProduct={onDeleteProduct}
        onSelectProduct={onSelectProduct}
      />
      <FlexRow className={styles['add-box-button']}>
        <Button onClick={onAddNewBoxConfiguration} theme='white-outlined-pine'>
          <FlexRow>
            <FontAwesomeIcon icon={faPlus} />
            <Text
              textType={TextType.Caption}
              value={isParcelConfiguration ? messages.addBoxConfiguration : messages.addPalletConfiguration}
            />
          </FlexRow>
        </Button>
      </FlexRow>
      {isOpen ? (
        <ConfirmDialogueModal
          body={<Text textType={TextType.Paragraph} value={modalMessages.body} />}
          buttonTheme='danger'
          header={<Text textType={TextType.Paragraph} value={modalMessages.header} />}
          onClose={close}
          onConfirm={onClearAllBoxes}
        />
      ) : null}
    </>
  );
};
